import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of as _observableOf, throwError as _observableThrow } from 'rxjs';
import { catchError as _observableCatch, mergeMap as _observableMergeMap } from 'rxjs/operators';
import { CommonFunctions } from '../common-functions';
import { ExpenseCategoryBreakdownDtoListApiResponse } from '../models/expense-category-breakdown-dto-list-api-response.model';
import { InternFeeSummaryDtoListApiResponse } from '../models/intern-fee-summary-dto-list-api-response.model';
import { ProjectPnLDtoListApiResponse } from '../models/project-pn-l-dto-list-api-response.model';
import { API_BASE_URL } from '../service-proxies';

@Injectable()
export class ExpenseDashboardServiceProxy {
    private http: HttpClient;
    private baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(private _commonFunctions: CommonFunctions, @Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
    }

    /**
     * @param year (optional) 
     * @param month (optional) 
     * @return Success
     */
    getProjectMonthlyPnL(year: number | undefined, month: number | undefined): Observable<ProjectPnLDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExpenseDashboard/GetProjectMonthlyPnL?";
        if (year === null)
            throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined)
            url_ += "year=" + encodeURIComponent("" + year) + "&";
        if (month === null)
            throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined)
            url_ += "month=" + encodeURIComponent("" + month) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetProjectMonthlyPnL(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetProjectMonthlyPnL(<any>response_);
                } catch (e) {
                    return <Observable<ProjectPnLDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ProjectPnLDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetProjectMonthlyPnL(response: HttpResponseBase): Observable<ProjectPnLDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ProjectPnLDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ProjectPnLDtoListApiResponse>(<any>null);
    }

    /**
     * @param year (optional) 
     * @param month (optional) 
     * @return Success
     */
    getExpenseBreakdown(year: number | undefined, month: number | undefined): Observable<ExpenseCategoryBreakdownDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExpenseDashboard/GetExpenseBreakdown?";
        if (year === null)
            throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined)
            url_ += "year=" + encodeURIComponent("" + year) + "&";
        if (month === null)
            throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined)
            url_ += "month=" + encodeURIComponent("" + month) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetExpenseBreakdown(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetExpenseBreakdown(<any>response_);
                } catch (e) {
                    return <Observable<ExpenseCategoryBreakdownDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ExpenseCategoryBreakdownDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetExpenseBreakdown(response: HttpResponseBase): Observable<ExpenseCategoryBreakdownDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ExpenseCategoryBreakdownDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ExpenseCategoryBreakdownDtoListApiResponse>(<any>null);
    }

    /**
     * @param year (optional) 
     * @param month (optional) 
     * @return Success
     */
    getInternFeeSummary(year: number | undefined, month: number | undefined): Observable<InternFeeSummaryDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExpenseDashboard/GetInternFeeSummary?";
        if (year === null)
            throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined)
            url_ += "year=" + encodeURIComponent("" + year) + "&";
        if (month === null)
            throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined)
            url_ += "month=" + encodeURIComponent("" + month) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetInternFeeSummary(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetInternFeeSummary(<any>response_);
                } catch (e) {
                    return <Observable<InternFeeSummaryDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<InternFeeSummaryDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetInternFeeSummary(response: HttpResponseBase): Observable<InternFeeSummaryDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = InternFeeSummaryDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<InternFeeSummaryDtoListApiResponse>(<any>null);
    }
}
